// Share: everything that takes the page out of the editor. The public link on
// every host that serves it (the app's own host at /p/<slug>, and each custom
// domain at /<slug>, or / when the page is that domain's homepage), Set as
// homepage (superadmins), the embed code, and a way to connect a domain. A page
// that is not live yet offers Publish instead. Below them: the page link and
// how the page shows when shared or searched (title, description, social image,
// favicon), the same fields as the Page tab's.
import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { bestPublicUrl, publicPageUrl, type PageDomain } from '../api';
import { CopyIcon, ExternalIcon } from '../icons';
import { useConfirm, useToast } from '../ui/feedback';
import { EditorDrawer } from './EditorDrawer';
import { useEditorApi } from './editorApi';
import { INSPECTOR_SCOPE, InspectorReadOnly } from './InspectorKit';
import { PageLinkField, SeoFields, type PageSeo, type SlugMessage } from './SearchSharing';
import { embedCode as attributionEmbed } from '../../crmAttribution';

/** The embed code for a page link. */
export function embedCode(url: string, title: string): string {
  const slug = url.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9_-]/g, '-');
  return attributionEmbed(url, slug, title, { page: true });
}

/** "host.com" from "https://host.com" (the origin itself if it does not parse). */
function hostOf(origin: string): string {
  try { return new URL(origin).host; } catch { return origin.replace(/^https?:\/\//, ''); }
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const STATUS_NOTE: Record<PageDomain['status'], string> = {
  active: '',
  pending: 'Waiting for DNS',
  error: 'Needs attention',
};

/** The editable link and search/share fields (PageEditor's state). */
export interface ShareSettings {
  readOnly: boolean;
  /** The link being edited (page.slug; may differ from the saved one). */
  draftSlug: string;
  onSlugChange: (slug: string) => void;
  slugMsg: SlugMessage;
  slugBad: boolean;
  seo: PageSeo;
  seoFallback: { title: string; description: string; image: string; favicon: string };
  setSeo: (p: Partial<PageSeo>) => void;
  host: string;
  hosts: string[];
  onHostChange: (host: string) => void;
  /** A live page has edits visitors don't see yet. */
  unpublished: boolean;
}

export function ShareDrawer({
  open, onClose, live, pageId, pageName, slug, appOrigin, domains, canPublish, canSetHomepage, onPublish, publishing, onDomainChange, settings,
}: {
  open: boolean;
  onClose: () => void;
  live: boolean;
  pageId: string;
  pageName: string;
  /** The saved page link (a changed link is not real until it saves). */
  slug: string;
  /** The app's own origin (https://host). */
  appOrigin: string;
  /** Connected custom domains (any status). */
  domains: PageDomain[];
  canPublish: boolean;
  /** Superadmins pick a domain's homepage. */
  canSetHomepage: boolean;
  onPublish: () => void;
  publishing: boolean;
  /** A domain the panel changed (its homepage). */
  onDomainChange: (d: PageDomain) => void;
  settings?: ShareSettings;
}) {
  const api = useEditorApi();
  const toast = useToast();
  const confirm = useConfirm();
  const embedId = useId();
  const [busyId, setBusyId] = useState<number | null>(null);
  const appUrl = publicPageUrl(slug, { origin: appOrigin });
  // Embeds point at the address visitors should see: a live custom domain first.
  const embed = embedCode(bestPublicUrl({ id: pageId, slug }, domains, appOrigin), pageName || 'Page');

  async function copy(text: string, what: string) {
    if (await copyText(text)) toast(`${what} copied`);
    else toast('Couldn’t copy. Select it and copy it yourself.', 'error');
  }

  async function setHomepage(d: PageDomain) {
    const current = d.root_page && d.root_page.id !== pageId ? d.root_page.name : '';
    const ok = await confirm({
      title: 'Make this the homepage?',
      body: current
        ? `Visitors to ${d.domain} will land on this page instead of “${current}”.`
        : `Visitors to ${d.domain} will land on this page.`,
      confirmLabel: 'Set homepage',
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      onDomainChange(await api.setDomainRoot(d.id, pageId));
      toast(`This page is now the homepage of ${d.domain}`);
    } catch (e) {
      toast((e as Error).message || 'Couldn’t set the homepage. Try again.', 'error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <EditorDrawer open={open} onClose={onClose} title="Share">
      <div className="grid gap-5" data-share-drawer>
        {live ? (
          <div className="grid min-w-0 gap-1.5">
            <span className="label !mb-0">Public link</span>
            <LinkRow label={hostOf(appOrigin)} url={appUrl} onCopy={() => copy(appUrl, 'Link')} />
            {domains.map((d) => {
              const home = d.root_page_id === pageId;
              const url = publicPageUrl(slug, { domain: d });
              const ready = d.status === 'active';
              return (
                <LinkRow key={d.id} label={d.domain} url={url} disabled={!ready} onCopy={() => copy(url, 'Link')}
                  badge={home
                    ? <span className="pe-badge chip-blue">Homepage</span>
                    : !ready ? <span className="pe-badge chip-amber">{STATUS_NOTE[d.status]}</span> : null}>
                  {canSetHomepage && ready && !home && (
                    <button type="button" className="btn-ghost btn-sm justify-self-start" disabled={busyId !== null}
                      onClick={() => { void setHomepage(d); }}>
                      {busyId === d.id ? 'Setting…' : 'Set as homepage'}
                    </button>
                  )}
                </LinkRow>
              );
            })}
            <Link to="/pages/domains" className="justify-self-start text-xs font-medium text-primary underline-offset-2 hover:underline">
              {domains.length ? 'Manage domains' : 'Connect a custom domain'}
            </Link>
          </div>
        ) : (
          <div className={`grid justify-items-center gap-1.5 px-4 text-center ${settings ? 'rounded-lg border border-border py-6' : 'py-10'}`}>
            <p className="text-base font-semibold text-text">Not published yet</p>
            <p className="text-sm text-textDim">Publish the page to get its public link and embed code.</p>
            {canPublish && (
              <button type="button" className="btn-primary mt-2.5" disabled={publishing} onClick={onPublish}>
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            )}
          </div>
        )}
        {settings && <ShareSettingsFields live={live} settings={settings} canPublish={canPublish} publishing={publishing} onPublish={onPublish} />}
        {live && (
          <div className="grid min-w-0 gap-1.5 border-t border-border pt-5">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <label htmlFor={embedId} className="label !mb-0">Embed on your site</label>
              <button type="button" className="btn-ghost btn-sm" onClick={() => { void copy(embed, 'Embed code'); }}>
                <CopyIcon size={13} />Copy
              </button>
            </div>
            <textarea id={embedId} className="input font-mono text-xs" readOnly value={embed} rows={4}
              onFocus={(e) => e.currentTarget.select()} />
          </div>
        )}
      </div>
    </EditorDrawer>
  );
}

/** One host's link: the host, a status or homepage badge, the link (opens in a
 *  new tab), Copy, and an optional action under it. */
function LinkRow({ label, url, badge, disabled = false, onCopy, children }: {
  label: string;
  url: string;
  badge?: ReactNode;
  /** The host does not serve the page yet (DNS pending). */
  disabled?: boolean;
  onCopy: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="grid min-w-0 gap-1.5 rounded-lg border border-border px-3 py-2.5" data-share-link={url}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-xs text-textDim">{label}</span>
        {badge}
      </div>
      <div className="flex min-w-0 items-center gap-1">
        {disabled ? (
          <span className="min-w-0 flex-1 truncate text-sm text-textDim">{url}</span>
        ) : (
          <a href={url} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm text-primary underline-offset-2 hover:underline">
            <span className="min-w-0 truncate">{url}</span>
            <ExternalIcon size={13} className="shrink-0" />
          </a>
        )}
        <button type="button" className="pe-icon-btn" aria-label={`Copy ${url}`} title="Copy link" disabled={disabled} onClick={onCopy}>
          <CopyIcon size={15} />
        </button>
      </div>
      {children}
    </div>
  );
}

/** The page link and the search/share fields, editable in place. */
function ShareSettingsFields({ live, settings, canPublish, publishing, onPublish }: {
  live: boolean;
  settings: ShareSettings;
  canPublish: boolean;
  publishing: boolean;
  onPublish: () => void;
}) {
  const s = settings;
  return (
    <InspectorReadOnly.Provider value={s.readOnly}>
      <fieldset className={`pe-readonly ${INSPECTOR_SCOPE}`} disabled={s.readOnly} data-share-settings>
        <div className="grid min-w-0 gap-3 border-t border-border pt-5">
          <PageLinkField slug={s.draftSlug} onChange={s.onSlugChange} msg={s.slugMsg} bad={s.slugBad}
            hint={live ? 'If you change it, the old link stops working once you publish.' : undefined} />
        </div>
        <div className="grid min-w-0 gap-3 border-t border-border pt-5">
          <div className="grid gap-0.5">
            <h3 className="m-0 text-sm font-semibold text-text">Search and sharing</h3>
            <p className="m-0 text-xs text-textDim">How the page looks in Google, in a browser tab and when its link is shared. Blank fields use the page’s headline and first image.</p>
          </div>
          <SeoFields seo={s.seo} fallback={s.seoFallback} setSeo={s.setSeo} host={s.host} hosts={s.hosts} onHostChange={s.onHostChange} />
        </div>
        {live && s.unpublished && canPublish && (
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg bg-surface2 px-3 py-2.5" data-share-unpublished>
            <span className="text-sm text-text">Your changes aren’t live yet.</span>
            <button type="button" className="btn-primary btn-sm" disabled={publishing} onClick={onPublish}>
              {publishing ? 'Publishing…' : 'Publish changes'}
            </button>
          </div>
        )}
      </fieldset>
    </InspectorReadOnly.Provider>
  );
}
